import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  Fade,
  IconButton
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Warning,
  Circle,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import { Issue, Sprint } from '../../types';
import { format, differenceInDays, isAfter, isBefore, addDays, parseISO, startOfDay, endOfDay } from 'date-fns';

interface SprintTimelineViewProps {
  sprint?: Sprint;
  issues?: Issue[];
  height?: number;
  boardType?: 'sprint' | 'kanban' | 'scrum' | 'bau';
  boardName?: string;
}

interface TimelineTask {
  issue: Issue;
  startDate: Date;
  endDate: Date;
  left: number;
  width: number;
  status: 'completed' | 'overdue' | 'near-due' | 'on-track';
  daysUntilDue: number;
}

const SprintTimelineView: React.FC<SprintTimelineViewProps> = ({ 
  sprint, 
  issues = [],
  height = 500,
  boardType = 'sprint',
  boardName = 'Board'
}) => {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const today = startOfDay(new Date());

  // Calculate timeline dimensions
  const timelineData = useMemo(() => {
    let start: Date;
    let end: Date;
    let duration: number;

    if (boardType === 'sprint' && sprint) {
      start = startOfDay(parseISO(sprint.startDate));
      end = endOfDay(parseISO(sprint.endDate));
      duration = differenceInDays(end, start);
    } else {
      // Kanban: use story due dates
      const datesWithDue = issues.filter(i => i.dueDate).map(i => parseISO(i.dueDate!));
      if (datesWithDue.length > 0) {
        const earliest = new Date(Math.min(...datesWithDue.map(d => d.getTime())));
        const latest = new Date(Math.max(...datesWithDue.map(d => d.getTime())));
        start = isBefore(earliest, today) ? earliest : today;
        end = isAfter(latest, addDays(start, 14)) ? latest : addDays(start, 21);
      } else {
        start = today;
        end = addDays(today, 21);
      }
      duration = differenceInDays(end, start);
    }

    return { start, end, duration };
  }, [sprint, issues, boardType, today]);

  // Calculate tasks for timeline
  const tasks = useMemo(() => {
    const { start, end, duration } = timelineData;
    const dayWidth = Math.max(80, duration > 0 ? 100 : 100);
    const totalWidth = duration * dayWidth;

    return issues
      .map((issue): TimelineTask | null => {
        let taskStart: Date;
        let taskEnd: Date;

        if (issue.dueDate) {
          taskEnd = startOfDay(parseISO(issue.dueDate));
          taskStart = isBefore(addDays(taskEnd, -3), start) ? start : addDays(taskEnd, -3);
          
          if (isBefore(taskStart, start)) taskStart = start;
          if (isAfter(taskEnd, end)) taskEnd = end;
        } else {
          // Distribute evenly
          const index = issues.indexOf(issue);
          const daysFromStart = (index * duration) / issues.length;
          taskStart = addDays(start, Math.floor(daysFromStart));
          taskEnd = addDays(taskStart, Math.max(1, duration / issues.length));
        }

        // Calculate position
        const daysFromStart = differenceInDays(taskStart, start);
        const taskDuration = differenceInDays(taskEnd, taskStart);
        
        const left = (daysFromStart / duration) * totalWidth;
        const width = Math.max(40, (taskDuration / duration) * totalWidth);

        // Determine status
        const isCompleted = issue.status === 'done' || issue.status === 'closed';
        let status: 'completed' | 'overdue' | 'near-due' | 'on-track';
        let daysUntilDue = 0;

        if (isCompleted) {
          status = 'completed';
        } else if (issue.dueDate) {
          daysUntilDue = differenceInDays(parseISO(issue.dueDate), today);
          if (daysUntilDue < 0) {
            status = 'overdue';
          } else if (daysUntilDue <= 3) {
            status = 'near-due';
          } else {
            status = 'on-track';
          }
        } else {
          status = 'on-track';
        }

        return {
          issue,
          startDate: taskStart,
          endDate: taskEnd,
          left,
          width,
          status,
          daysUntilDue
        };
      })
      .filter((task): task is TimelineTask => task !== null)
      .sort((a, b) => a.left - b.left);
  }, [issues, timelineData, today]);

  // Generate date ticks
  const dateTicks = useMemo(() => {
    const { start, end, duration } = timelineData;
    const dayWidth = Math.max(80, duration > 0 ? 100 : 100);
    const totalWidth = duration * dayWidth;
    const ticks: Array<{ date: Date; position: number; label: string }> = [];

    // Show start date
    ticks.push({
      date: start,
      position: 0,
      label: format(start, 'MMM d, yyyy')
    });

    // Show end date
    ticks.push({
      date: end,
      position: totalWidth,
      label: format(end, 'MMM d, yyyy')
    });

    // Show intermediate dates (every 3-4 days or so)
    const interval = Math.max(1, Math.floor(duration / 8));
    for (let i = interval; i < duration; i += interval) {
      const date = addDays(start, i);
      ticks.push({
        date,
        position: (i / duration) * totalWidth,
        label: format(date, 'MMM d')
      });
    }

    return ticks.sort((a, b) => a.position - b.position);
  }, [timelineData]);

  // Get status color
  const getStatusColor = (status: TimelineTask['status']) => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main;
      case 'overdue':
        return theme.palette.error.main;
      case 'near-due':
        return theme.palette.warning.main;
      case 'on-track':
        return theme.palette.info.main;
    }
  };

  // Handle scroll
  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  // Update scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setScrollPosition(container.scrollLeft);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const { start, end, duration } = timelineData;
  const dayWidth = Math.max(80, duration > 0 ? 100 : 100);
  const totalWidth = duration * dayWidth;
  const todayPosition = duration > 0 
    ? (differenceInDays(today, start) / duration) * totalWidth 
    : 0;
  const isTodayVisible = todayPosition >= 0 && todayPosition <= totalWidth;

  // Stats
  const stats = useMemo(() => {
    const completed = tasks.filter(t => t.status === 'completed').length;
    const overdue = tasks.filter(t => t.status === 'overdue').length;
    const nearDue = tasks.filter(t => t.status === 'near-due').length;
    const onTrack = tasks.filter(t => t.status === 'on-track').length;
    return { completed, overdue, nearDue, onTrack, total: tasks.length };
  }, [tasks]);

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      {/* Stats Bar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        flexWrap: 'wrap',
        pb: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Chip
          icon={<CheckCircle />}
          label={`${stats.completed} Completed`}
          color="success"
          size="small"
          sx={{ fontWeight: 500 }}
        />
        <Chip
          icon={<Circle />}
          label={`${stats.onTrack} On Track`}
          sx={{ 
            bgcolor: alpha(theme.palette.info.main, 0.1), 
            color: theme.palette.info.dark,
            fontWeight: 500
          }}
          size="small"
        />
        <Chip
          icon={<Schedule />}
          label={`${stats.nearDue} Near Due`}
          color="warning"
          size="small"
          sx={{ fontWeight: 500 }}
        />
        <Chip
          icon={<Warning />}
          label={`${stats.overdue} Overdue`}
          color="error"
          size="small"
          sx={{ fontWeight: 500 }}
        />
      </Box>

      {/* Timeline Container */}
      <Paper 
        elevation={2}
        sx={{ 
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        {/* Scroll Controls */}
        <Box sx={{ 
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 1,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          borderRadius: 1,
          p: 0.5,
          boxShadow: theme.shadows[2]
        }}>
          <IconButton 
            size="small"
            onClick={() => handleScroll('left')}
            sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
            }}
          >
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>
          <IconButton 
            size="small"
            onClick={() => handleScroll('right')}
            sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
            }}
          >
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable Container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            overflowX: 'auto',
            overflowY: 'visible',
            '&::-webkit-scrollbar': {
              height: '12px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.divider, 0.1),
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.3),
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.5),
              },
            },
          }}
        >
          <Box sx={{ 
            position: 'relative',
            width: `${totalWidth}px`,
            minHeight: `${height}px`,
            bgcolor: alpha(theme.palette.background.default, 0.3),
            p: 3
          }}>
            {/* Timeline Header */}
            <Box sx={{ 
              position: 'sticky',
              top: 0,
              zIndex: 5,
              bgcolor: theme.palette.background.paper,
              borderBottom: `2px solid ${theme.palette.divider}`,
              mb: 3,
              pb: 2,
              pt: 1
            }}>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="caption" sx={{ 
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 1
                }}>
                  {boardType === 'sprint' && sprint 
                    ? `Sprint: ${sprint.name}` 
                    : boardName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Start: {format(start, 'MMM d, yyyy')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    End: {format(end, 'MMM d, yyyy')}
                  </Typography>
                </Box>
              </Box>

              {/* Date Ticks */}
              <Box sx={{ position: 'relative', height: 40, mt: 2 }}>
                {dateTicks.map((tick, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'absolute',
                      left: `${tick.position}px`,
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        width: index === 0 || index === dateTicks.length - 1 ? 2 : 1,
                        height: 16,
                        bgcolor: theme.palette.text.primary,
                        mb: 0.5
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: index === 0 || index === dateTicks.length - 1 ? '0.75rem' : '0.7rem',
                        fontWeight: index === 0 || index === dateTicks.length - 1 ? 700 : 500,
                        color: theme.palette.text.primary,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {tick.label}
                    </Typography>
                  </Box>
                ))}

                {/* Today Indicator */}
                {isTodayVisible && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${todayPosition}px`,
                      top: 0,
                      bottom: -16,
                      width: 2,
                      bgcolor: theme.palette.primary.main,
                      zIndex: 6,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -24,
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        boxShadow: theme.shadows[3]
                      }}
                    >
                      Today
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Tasks/Gantt Bars */}
            <Box sx={{ position: 'relative', mt: 4 }}>
              {tasks.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8, 
                  color: theme.palette.text.secondary 
                }}>
                  <Typography variant="body2">
                    No tasks to display
                  </Typography>
                </Box>
              ) : (
                tasks.map((task, index) => {
                  const statusColor = getStatusColor(task.status);
                  const isCompleted = task.status === 'completed';

                  return (
                    <Tooltip
                      key={task.issue.id}
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 700, 
                            mb: 1,
                            color: 'white'
                          }}>
                            {task.issue.title}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 0.5, color: alpha('#fff', 0.9) }}>
                            Status: <strong>{task.issue.status}</strong>
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 0.5, color: alpha('#fff', 0.9) }}>
                            Due: {format(task.endDate, 'MMM d, yyyy')}
                          </Typography>
                          {task.issue.storyPoints && (
                            <Typography variant="caption" display="block" sx={{ mb: 0.5, color: alpha('#fff', 0.9) }}>
                              Points: {task.issue.storyPoints}
                            </Typography>
                          )}
                          {task.issue.assignee && (
                            <Typography variant="caption" display="block" sx={{ color: alpha('#fff', 0.9) }}>
                              Assignee: {task.issue.assignee.firstName} {task.issue.assignee.lastName}
                            </Typography>
                          )}
                          {task.daysUntilDue !== 0 && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: alpha('#fff', 0.9) }}>
                              {task.daysUntilDue > 0 
                                ? `${task.daysUntilDue} days remaining`
                                : `${Math.abs(task.daysUntilDue)} days overdue`}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      TransitionComponent={Fade}
                      placement="top"
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${task.left}px`,
                          top: `${index * 60}px`,
                          width: `${task.width}px`,
                          height: 44,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            zIndex: 10
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: isCompleted 
                              ? alpha(statusColor, 0.2)
                              : alpha(statusColor, 0.15),
                            border: `2px solid ${statusColor}`,
                            borderRadius: 2,
                            boxShadow: theme.shadows[2],
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: theme.shadows[6],
                              borderWidth: 3
                            }
                          }}
                        >
                          {/* Status Indicator Dot */}
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: statusColor,
                              mr: 1,
                              flexShrink: 0,
                              boxShadow: `0 0 8px ${alpha(statusColor, 0.5)}`
                            }}
                          />

                          {/* Task Title */}
                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: theme.palette.text.primary
                            }}
                          >
                            {task.issue.title}
                          </Typography>

                          {/* Story Points Badge */}
                          {task.issue.storyPoints && (
                            <Chip
                              label={task.issue.storyPoints}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                bgcolor: alpha(statusColor, 0.2),
                                color: statusColor,
                                border: `1px solid ${alpha(statusColor, 0.3)}`,
                                ml: 1
                              }}
                            />
                          )}

                          {/* Progress Indicator for In Progress */}
                          {task.issue.status === 'in_progress' && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                bgcolor: alpha(statusColor, 0.6),
                                animation: 'pulse 2s ease-in-out infinite',
                                '@keyframes pulse': {
                                  '0%, 100%': { opacity: 0.6 },
                                  '50%': { opacity: 1 }
                                }
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SprintTimelineView;

